import BasePage from './BasePage.js';
import InquiryApi from '../api/InquiryApi.js';

/**
 * 問い合わせページ
 */
export default class InquiryPage extends BasePage {
  #api;
  #submitSuccess = false;
  #backBtnHandler;
  #backToResultBtnHandler;
  #formSubmitHandler;

  /**
   * @param {Object} app SouzokuDiagnosisAppインスタンス
   */
  constructor(app) {
    super(app);
    this.#api = new InquiryApi();
  }

  /**
   * ページをレンダリング
   */
  render() {
    const firmData = window.FIRM_DATA;

    if (this.#submitSuccess) {
      // 送信成功画面
      this._container.innerHTML = `
        <!-- 成功メッセージ -->
        <div class="text-center mb-6">
          <h2 class="fw-bold mb-3 fs-3">お問い合わせありがとうございます</h2>
          <p class="fs-6 mb-0">3営業日以内にご連絡いたします。<br>お急ぎの場合は、お電話でお問い合わせください。</p>
        </div>

        <!-- 事務所情報カード -->
        <div class="mb-8">
          ${this.#buildFirmInfoCard()}
        </div>

        <!-- 戻るボタン -->
        <div class="fixed-footer">
          <button type="button" class="btn btn-primary w-100" data-ref="backToResultBtn">
            診断結果に戻る
          </button>
        </div>
      `;
    } else {
      // 問い合わせフォーム
      this._container.innerHTML = `
        <!-- タイトル -->
        <div class="mb-10 text-center">
          <h1 class="mb-3">お問い合わせ／診断結果送信</h1>
          <div class="fw-semibold fs-6">必要事項をご入力のうえ送信してください。ご入力いただいたメールアドレス宛に診断結果をお届けします。ご相談も承ります。</div>
        </div>

        <!-- フォーム -->
        <form id="inquiryForm" novalidate>
          <div class="mb-5">
            <label class="form-label required">氏名（ニックネーム可）</label>
            <input type="text" class="form-control form-control-solid" name="name" placeholder="山田 太郎" maxlength="30" required>
          </div>

          <div class="mb-5">
            <label class="form-label required">メールアドレス</label>
            <input type="email" class="form-control form-control-solid" name="email" placeholder="example@email.com" maxlength="100" required>
          </div>

          <div class="mb-5">
            <label class="form-label">電話番号</label>
            <input type="tel" class="form-control form-control-solid" name="phone" placeholder="03-1234-5678" maxlength="20">
          </div>

          <div class="mb-5">
            <label class="form-label">郵便番号</label>
            <input type="text" class="form-control form-control-solid" name="postalCode" placeholder="100-0001" maxlength="10">
          </div>

          <div class="mb-5">
            <label class="form-label">住所</label>
            <input type="text" class="form-control form-control-solid" name="address" placeholder="東京都千代田区丸の内1-2-3" maxlength="100">
          </div>

          <div class="mb-5">
            <div class="form-check form-check-custom form-check-solid">
              <input class="form-check-input" type="checkbox" name="requestConsultation" id="requestConsultation">
              <label class="form-check-label" for="requestConsultation">
                個別相談を希望する
              </label>
            </div>
          </div>

          <div class="mb-5">
            <label class="form-label">問い合わせ内容</label>
            <textarea class="form-control form-control-solid" name="message" rows="5" placeholder="お問い合わせ内容をご記入ください" maxlength="500"></textarea>
          </div>

          <!-- ボタン -->
          <div class="fixed-footer d-flex gap-3 mt-10">
            <button type="button" class="btn btn-outline btn-outline-secondary" style="flex: 1 1 0;" data-ref="backBtn">
              <i class="ki-duotone ki-arrow-left fs-3 me-1">
                <span class="path1"></span>
                <span class="path2"></span>
              </i>
              戻る
            </button>
            <button type="submit" class="btn btn-primary" style="flex: 1 1 0;" data-ref="submitBtn">
              <span class="indicator-label">送信</span>
              <span class="indicator-progress">送信中...
              <span class="spinner-border spinner-border-sm align-middle ms-2"></span></span>
            </button>
          </div>
        </form>
      `;
    }

    this.bindEvents();
  }

  /**
   * イベントをバインド
   */
  bindEvents() {
    if (this.#submitSuccess) {
      // 成功画面: 診断結果に戻るボタン
      const backToResultBtn = this._container.querySelector('[data-ref="backToResultBtn"]');

      if (!this.#backToResultBtnHandler) {
        this.#backToResultBtnHandler = () => {
          // 成功フラグをリセット
          this.#submitSuccess = false;
          // 診断結果ページに戻る（prevStepでcurrentStepが2に戻る）
          this._app.prevStep();
        };
      }

      backToResultBtn?.removeEventListener('click', this.#backToResultBtnHandler);
      backToResultBtn?.addEventListener('click', this.#backToResultBtnHandler);
    } else {
      // フォーム画面: 戻るボタン
      const backBtn = this._container.querySelector('[data-ref="backBtn"]');

      if (!this.#backBtnHandler) {
        this.#backBtnHandler = () => {
          this._app.prevStep();
        };
      }

      backBtn?.removeEventListener('click', this.#backBtnHandler);
      backBtn?.addEventListener('click', this.#backBtnHandler);

      // フォーム送信
      const form = this._container.querySelector('#inquiryForm');

      if (!this.#formSubmitHandler) {
        this.#formSubmitHandler = async (e) => {
          e.preventDefault();
          await this.#submitInquiry();
        };
      }

      form?.removeEventListener('submit', this.#formSubmitHandler);
      form?.addEventListener('submit', this.#formSubmitHandler);
    }
  }

  /**
   * 問い合わせを送信
   * @private
   */
  async #submitInquiry() {
    const form = this._container.querySelector('#inquiryForm');
    const formData = new FormData(form);

    // カスタムバリデーション
    const name = formData.get('name')?.trim();
    const phone = formData.get('phone')?.trim();
    const email = formData.get('email')?.trim();

    const errors = [];

    if (!name) {
      errors.push('・氏名を入力してください');
    }

    // 電話番号は任意だが、入力されている場合は形式チェック
    if (phone) {
      const phonePattern = /^[\d\-\(\)\s]+$/;
      if (!phonePattern.test(phone)) {
        errors.push('・電話番号は数字とハイフンで入力してください');
      } else if (phone.replace(/[\-\(\)\s]/g, '').length < 10) {
        errors.push('・電話番号は10桁以上で入力してください');
      }
    }

    if (!email) {
      errors.push('・メールアドレスを入力してください');
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(email)) {
        errors.push('・メールアドレスの形式が正しくありません');
      }
    }

    // エラーがある場合は一括表示
    if (errors.length > 0) {
      alert('入力内容に誤りがあります:\n\n' + errors.join('\n'));
      return;
    }

    const submitBtn = this._container.querySelector('[data-ref="submitBtn"]');

    try {
      // ローディング表示
      submitBtn.disabled = true;
      submitBtn.setAttribute('data-kt-indicator', 'on');

      const data = {
        name: formData.get('name'),
        phone: formData.get('phone'),
        postalCode: formData.get('postalCode'),
        address: formData.get('address'),
        email: formData.get('email'),
        message: formData.get('message'),
        requestConsultation: formData.get('requestConsultation') === 'on',
        firmId: window.FIRM_DATA.id,
      };

      // メールアドレスが入力されている場合は診断結果データも送信
      // （ユーザーへPDF送信するため）
      if (email && this._app.data.diagnosisResult) {
        data.diagnosisResult = this._app.data.diagnosisResult;
      }

      await this.#api.send(data);

      // 送信成功フラグを立てて再レンダリング
      this.#submitSuccess = true;
      this.render();

      // スクロールを一番上に
      window.scrollTo({top: 0, behavior: 'smooth'});
    } catch (error) {
      console.error('問い合わせ送信エラー:', error);
      alert('問い合わせの送信に失敗しました。もう一度お試しください。');
    } finally {
      // ローディング解除
      submitBtn?.removeAttribute('data-kt-indicator');
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  /**
   * 事務所情報カードを生成
   * @private
   * @returns {string} 事務所情報カードのHTML
   */
  #buildFirmInfoCard() {
    const firm = window.FIRM_DATA || {};

    return `
      <div class="card">
        <div class="card-body">
          <!-- 基本情報（中央配置） -->
          <div class="text-center mb-4">
            <h5 class="mb-2 fw-bold">${firm.name || ''}</h5>
            <p class="mb-1 fs-6">住所: ${firm.address || ''}</p>
            <p class="mb-0 fs-6">相続鑑定士: ${firm.representativeName || ''}</p>
            ${firm.description ? `<p class="mt-3 mb-0 fs-6">${firm.description}</p>` : ''}
          </div>

          <!-- アイキャッチ画像 -->
          <div class="mb-4">
            <img src="${firm.eyecatchPath || ''}" alt="${firm.name || ''}" class="w-100 rounded">
          </div>

          <!-- メッセージ -->
          <p class="text-center mb-4">
            ご相談をお待ちしております。<br>
            お気軽にお問い合わせください。
          </p>

          <!-- 連絡先（TEL強調） -->
          <div class="text-center">
            <p class="mb-2 fs-3 fw-bold text-primary">
              TEL: <a href="tel:${firm.phone || ''}" class="text-primary" style="text-decoration: underline;">${firm.phone || ''}</a>
            </p>
            <p class="mb-0 fs-6">
              メール: <a href="mailto:${firm.email || ''}" class="text-primary" style="text-decoration: underline;">${firm.email || ''}</a>
            </p>
          </div>
        </div>
      </div>
    `;
  }
}
